import {
  memo,
  type PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Helmet } from "react-helmet";
import Cookies from "js-cookie";

import { tenantConfig } from "@tenant-config";
import { DEFAULT_THEME } from "@vega-core/constants/global";
import { getTenantThemes } from "@vega-core/themeConfig/tenantThemes";
import { type ThemesList } from "@vega-core/themeConfig/themesModel";
import { isTruthy } from "@vega-core/utils/isTruthy";

import { ThemeContext } from "./ThemeContext";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const ReactHelmet = Helmet as unknown as any;

function getValidTheme(): ThemesList {
  const cookieTheme = Cookies.get("theme");
  const tenantThemes = getTenantThemes();

  if (isTruthy(cookieTheme) && cookieTheme in tenantThemes) {
    return cookieTheme as ThemesList;
  }

  return DEFAULT_THEME;
}

type Props = {
  themeOverride?: ThemesList;
};

export const ThemeProvider = memo(
  ({ children, themeOverride }: PropsWithChildren<Props>) => {
    const [theme, setTheme] = useState<ThemesList>(
      themeOverride ?? getValidTheme,
    );

    const [systemTheme, setSystemTheme] = useState<"dark" | "light">(
      window.matchMedia("(prefers-color-scheme: dark)").matches
        ? "dark"
        : "light",
    );

    useEffect(() => {
      if (themeOverride) setTheme(themeOverride);
    }, [themeOverride, theme]);

    const [useSystemTheme, setUseSystemTheme] = useState(
      Cookies.get("useSystemTheme") === "true" ? true : false,
    );

    // Listen for changes to the system theme preference
    useEffect(() => {
      const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");

      function handleSystemThemeChange(event: MediaQueryListEvent) {
        const newSystemTheme = event.matches ? "dark" : "light";
        setSystemTheme(newSystemTheme);
      }

      mediaQuery.addEventListener("change", handleSystemThemeChange);

      return () => {
        mediaQuery.removeEventListener("change", handleSystemThemeChange);
      };
    }, [theme]);

    useEffect(() => {
      Cookies.set("theme", theme);
      Cookies.set("useSystemTheme", useSystemTheme ? "true" : "false");
    }, [theme, useSystemTheme]);

    useEffect(() => {
      if (useSystemTheme && !themeOverride) {
        setTheme(systemTheme === "dark" ? "dark" : "light");
      }
    }, [useSystemTheme, systemTheme, themeOverride]);

    // Set tenant-specific CSS variables on document root
    useEffect(() => {
      const root = document.documentElement;
      const dataColors =
        theme === "dark"
          ? tenantConfig.branding.dataColors.dark
          : tenantConfig.branding.dataColors.light;

      dataColors.forEach((color: string, index: number) => {
        root.style.setProperty(`--data-${index + 1}`, color);
        root.style.setProperty(`--chart-${index + 1}`, color);
      });

      root.style.setProperty(
        "--company-border-radius",
        tenantConfig.branding.borderRadius,
      );
      root.style.setProperty(
        "--background-color",
        tenantConfig.branding.backgroundColor,
      );
    }, [theme]);

    const changeTheme = useCallback((themeValue: ThemesList) => {
      setTheme(themeValue);
    }, []);

    getTenantThemes();

    const contextValue = useMemo(
      () => ({
        changeTheme,
        setUseSystemTheme,
        theme,
        useSystemTheme,
      }),
      [setUseSystemTheme, useSystemTheme, changeTheme, theme],
    );

    return (
      <ThemeContext.Provider value={contextValue}>
        <ReactHelmet>
          <html className={theme} />
        </ReactHelmet>
        <div className={theme}>{children}</div>
      </ThemeContext.Provider>
    );
  },
);
